import type { AxiosInstance } from 'axios';
import type { SigningSession, SubmitFieldRequest } from '../types/envelope';

// Cache the client IP so we only fetch it once per session
let cachedClientIp: string | null = null;

async function getClientIp(): Promise<string> {
  if (cachedClientIp) return cachedClientIp;
  try {
    const { default: axios } = await import('axios');
    const res = await axios.get('https://api.ipify.org?format=json', { timeout: 3000 });
    cachedClientIp = res.data.ip;
    return cachedClientIp!;
  } catch {
    return ''; // Backend will use server-side detection as fallback
  }
}

// Cache geolocation
let cachedLocation: string | null = null;

function getLocation(): Promise<string> {
  if (cachedLocation) return Promise.resolve(cachedLocation);
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(''); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cachedLocation = `${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`;
        resolve(cachedLocation);
      },
      () => resolve(''),
      { timeout: 5000 },
    );
  });
}

async function getClientContext(): Promise<Record<string, string>> {
  const [clientIp, location] = await Promise.all([getClientIp(), getLocation()]);
  return {
    clientIp,
    location,
    userAgent: navigator.userAgent,
  };
}

export function createSigningService(client: AxiosInstance) {
  return {
    loadSession: (token: string) =>
      client.get<SigningSession>(`/v1/signing/${token}`).then(r => r.data),

    submitFields: (token: string, fields: SubmitFieldRequest[]) =>
      client.post(`/v1/signing/${token}/fields`, fields),

    completeSigning: async (token: string) => {
      const ctx = await getClientContext();
      return client.post(`/v1/signing/${token}/complete`, ctx);
    },

    declineSigning: async (token: string, reason: string) => {
      const ctx = await getClientContext();
      return client.post(`/v1/signing/${token}/decline`, { reason, ...ctx });
    },

    getDocument: (token: string, docId: string) =>
      client.get<Blob>(`/v1/signing/${token}/document/${docId}`, { responseType: 'blob' }),

    downloadDocument: (token: string, docId: string) =>
      client.get<Blob>(`/v1/signing/${token}/document/${docId}/download`, { responseType: 'blob' }),

    uploadSignedScan: (token: string, fileId: string) =>
      client.post(`/v1/signing/${token}/upload-signed`, { fileId }),

    acknowledge: async (token: string) => {
      const ctx = await getClientContext();
      return client.post(`/v1/signing/${token}/acknowledge`, ctx);
    },
  };
}
